import React, { useState } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
import { createProfileImageData, getAvailableProfileImages } from '../../utils/profileImageUtils';

const ProfileImageSelector = ({ 
  girlData, 
  onProfileImageUpdate,
  isOwner = false 
}) => {
  const [showSelector, setShowSelector] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);

  const availableImages = getAvailableProfileImages(girlData);

  const handleSelectProfileImage = async (image) => {
    if (!isOwner) return;

    setIsUpdating(true);
    try {
      const girlRef = doc(db, 'girls', girlData.id);
      const profileImageData = createProfileImageData(image);
      
      await updateDoc(girlRef, {
        profileImage: profileImageData,
        lastUpdated: new Date().toISOString()
      });

      alert('เปลี่ยนรูปภาพหลักสำเร็จ!');
      onProfileImageUpdate && onProfileImageUpdate();
      setShowSelector(false);
    } catch (error) {
      console.error('Error updating profile image:', error);
      alert('เกิดข้อผิดพลาดในการเปลี่ยนรูปภาพหลัก');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleRemoveProfileImage = async () => {
    if (!isOwner) return;

    if (!window.confirm('คุณแน่ใจหรือไม่ที่จะลบรูปภาพหลัก? ระบบจะใช้รูปภาพแรกที่อนุมัติแล้วแทน')) {
      return;
    }

    setIsUpdating(true);
    try {
      const girlRef = doc(db, 'girls', girlData.id);
      
      await updateDoc(girlRef, {
        profileImage: null,
        lastUpdated: new Date().toISOString()
      });

      alert('ลบรูปภาพหลักสำเร็จ!');
      onProfileImageUpdate && onProfileImageUpdate();
      setShowSelector(false);
    } catch (error) {
      console.error('Error removing profile image:', error);
      alert('เกิดข้อผิดพลาดในการลบรูปภาพหลัก');
    } finally {
      setIsUpdating(false);
    }
  };

  if (!isOwner) return null;

  return (
    <div className="space-y-4">
      {/* Current Profile Image Info */}
      <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
        <h3 className="font-semibold text-blue-800 mb-2">🖼️ รูปภาพหลักปัจจุบัน</h3>
        {girlData.profileImage ? (
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <img 
                src={girlData.profileImage.thumbnail || girlData.profileImage.url} 
                alt="Profile" 
                className="w-16 h-16 object-cover rounded-lg border-2 border-blue-300"
              />
              <div>
                <p className="text-sm font-medium text-blue-700">
                  {girlData.profileImage.fileName || 'รูปภาพหลัก'}
                </p>
                <p className="text-xs text-blue-600">
                  เลือกเมื่อ: {new Date(girlData.profileImage.selectedAt).toLocaleString('th-TH')}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setShowSelector(true)}
                className="px-3 py-1 bg-blue-500 text-white text-sm rounded hover:bg-blue-600 transition"
              >
                🔄 เปลี่ยนรูปภาพหลัก
              </button>
              <button
                onClick={handleRemoveProfileImage}
                disabled={isUpdating}
                className="px-3 py-1 bg-red-500 text-white text-sm rounded hover:bg-red-600 transition disabled:opacity-50"
              >
                🗑️ ลบรูปภาพหลัก
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-sm text-blue-700">
              ยังไม่ได้เลือกรูปภาพหลัก ระบบจะใช้รูปภาพแรกที่อนุมัติแล้ว
            </p>
            <button
              onClick={() => setShowSelector(true)}
              className="px-3 py-1 bg-blue-500 text-white text-sm rounded hover:bg-blue-600 transition"
            >
              📷 เลือกรูปภาพหลัก
            </button>
          </div>
        )}
      </div>

      {/* Profile Image Selector Modal */}
      {showSelector && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl max-h-[90vh] overflow-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">เลือกรูปภาพหลัก</h3>
              <button
                onClick={() => setShowSelector(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>
            
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                เลือกรูปภาพที่ต้องการใช้เป็นรูปภาพหลักในการแสดงในหน้าต่างๆ ของระบบ
              </p>
              
              {availableImages.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {availableImages.map((image, index) => (
                    <div
                      key={index}
                      className={`relative group cursor-pointer rounded-lg overflow-hidden border-2 ${
                        image.isCurrentProfile 
                          ? 'border-blue-500 ring-2 ring-blue-200' 
                          : 'border-gray-200 hover:border-blue-300'
                      }`}
                      onClick={() => setSelectedImage(image)}
                    >
                      <img
                        src={image.thumbnail || image.url}
                        alt={image.displayName}
                        className="w-full h-32 object-cover"
                      />
                      
                      {/* Status Overlay */}
                      <div className="absolute top-2 left-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          image.isCurrentProfile 
                            ? 'bg-blue-500 text-white' 
                            : 'bg-green-500 text-white'
                        }`}>
                          {image.isCurrentProfile ? '⭐ รูปหลัก' : '✅ อนุมัติแล้ว'}
                        </span>
                      </div>
                      
                      {/* Selection Overlay */}
                      {selectedImage && selectedImage.url === image.url && (
                        <div className="absolute inset-0 bg-blue-500 bg-opacity-30 flex items-center justify-center">
                          <span className="text-white text-2xl">✓</span>
                        </div>
                      )}
                      
                      {/* Image Info */}
                      <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white p-2">
                        <p className="text-xs truncate">{image.displayName}</p>
                        {image.originalName && (
                          <p className="text-xs opacity-75 truncate">{image.originalName}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p>ไม่มีรูปภาพที่อนุมัติแล้ว</p>
                  <p className="text-sm">กรุณาอัพโหลดรูปภาพและรอการอนุมัติจากแอดมิน</p>
                </div>
              )}
            </div>
            
            {/* Action Buttons */}
            <div className="flex gap-3 pt-4">
              <button
                onClick={() => setShowSelector(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
              >
                ยกเลิก
              </button>
              {selectedImage && (
                <button
                  onClick={() => handleSelectProfileImage(selectedImage)}
                  disabled={isUpdating}
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition disabled:opacity-50"
                >
                  {isUpdating ? 'กำลังอัพเดท...' : '✅ เลือกรูปภาพนี้'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileImageSelector;









